'use client';

import { useState, useEffect, useMemo } from 'react';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';
import { QRCodeSVG } from 'qrcode.react';
import { copyToClipboard } from 'src/utils/browser';
import { abbreviateAddress } from '@velocity-exchange/common';
import { Copy } from '@velocity-exchange/icons';
import useModal from 'src/components/Modals/hooks/useModal';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import TextButton from 'src/components/primitives/TextButton/TextButton';

const ReceiveToWalletModal = () => {
	const { hide } = useModal();
	const wallet = useCurrentWalletAdapter();
	const shortAddress = wallet?.publicKey
		? abbreviateAddress(wallet.publicKey)
		: '';
	const [copied, setCopied] = useState(false);

	const copyWalletAddress = () => {
		if (!copied && wallet?.publicKey) {
			copyToClipboard(wallet.publicKey.toString());
			setCopied(true);
		}
	};

	useEffect(() => {
		let resetCopiedTimeout: ReturnType<typeof setTimeout> | undefined;
		if (copied) {
			resetCopiedTimeout = setTimeout(() => {
				setCopied(false);
				resetCopiedTimeout = undefined;
			}, 3000);
		}
		return () => {
			if (resetCopiedTimeout) {
				clearTimeout(resetCopiedTimeout);
			}
		};
	}, [copied]);

	const walletString = useMemo(() => {
		return wallet?.publicKey?.toString();
	}, [wallet?.publicKey]);

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="receive-to-wallet-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="receive-to-wallet-modal-close">
				Receive Assets
			</ResponsiveModal.Title>
			<ResponsiveModal.Body className="items-center">
				<div className="rounded-sm bg-white p-md">
					<QRCodeSVG value={walletString ?? ''} size={180} />
				</div>

				<div className="flex w-full flex-col items-center gap-y-sm">
					<p>Your Wallet Address</p>
					<TextButton onClick={copyWalletAddress} rightIcon={Copy}>
						{copied ? 'Copied!' : shortAddress}
					</TextButton>
				</div>

				<p className="text-center">
					This address can only be used to receive assets via the Solana
					network.
				</p>
			</ResponsiveModal.Body>
		</ResponsiveModal.Container>
	);
};

export default ReceiveToWalletModal;
